import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { WidgetPreviewDialogComponent } from '../shared/widget-preview-dialog/widget-preview-dialog.component';

// Rendered answers are [innerHTML], so their links can't bind. A plain left click on a same-origin link
// stays in the app, and a campaign link opens its preview instead of leaving the chat.
export function openTranscriptLink(event: MouseEvent, dialog: MatDialog, router: Router): void {
  const link = transcriptLinkFor(event);
  if (!link) {
    return;
  }
  event.preventDefault();
  if (link.kind === 'campaign') {
    openCampaignPreview(dialog, link.campaignId, link.name);
  } else {
    void router.navigateByUrl(link.path);
  }
}

// The quick campaign preview shared with the activity log.
export function openCampaignPreview(dialog: MatDialog, campaignId: number, name: string): void {
  dialog.open(WidgetPreviewDialogComponent, {
    width: '400px',
    panelClass: 'widget-preview-dialog-component-container',
    data: { campaignId, currentPageTitle: name },
  });
}

type TranscriptLink = { kind: 'campaign'; campaignId: number; name: string } | { kind: 'route'; path: string };

// Null when the click keeps its native behaviour: an external, modified, fragment or new-tab link.
function transcriptLinkFor(event: MouseEvent): TranscriptLink | null {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return null;
  }
  const anchor = (event.target as HTMLElement | null)?.closest('a');
  if (!(anchor instanceof HTMLAnchorElement)) {
    return null;
  }
  const href = anchor.getAttribute('href') ?? '';
  if (href.length === 0 || href.startsWith('#') || anchor.target === '_blank') {
    return null;
  }
  let url: URL;
  try {
    url = new URL(anchor.href, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin || (url.protocol !== 'http:' && url.protocol !== 'https:')) {
    return null;
  }
  const campaignId = campaignIdFromPath(url.pathname);
  if (campaignId !== null) {
    return { kind: 'campaign', campaignId, name: anchor.textContent?.trim() || `#${campaignId}` };
  }
  return { kind: 'route', path: url.pathname + url.search + url.hash };
}

// Parses the /campaigns/<id> link the model emits (the Agents core.md contract); null otherwise.
function campaignIdFromPath(pathname: string): number | null {
  const match = /^\/campaigns\/(\d+)(?:\/|$)/.exec(pathname);
  return match ? Number(match[1]) : null;
}
